import React from "react";

import { filter, map, some } from "lodash/fp";

import useSearchMeetingsStore from "@app/analytics/hooks/meetings/useSearchMeetingsStore";
import useRecordingInfoQueries from "@app/analytics/hooks/recordings/useRecordingInfoQueries";
import MoreResults from "@app/analytics/MeetingReports/components/MoreResults";
import SearchResult from "@app/analytics/MeetingReports/components/SearchResult";
import { MAX_VISIBLE_MATCHES } from "@app/analytics/MeetingReports/components/SearchResult/utils";
import { SearchResult as ISearchResult } from "@app/analytics/types";
import usePagination, {
  DEFAULT_PAGE_SIZE as NUM_RESULTS_PER_PAGE,
} from "@app/common/hooks/usePagination";

import Stack from "@rui/Stack";

interface SearchResultListProps {
  query: string;
  results: ISearchResult[];
}

const getRecordingInfoSessionIds = (results: ISearchResult[]) => {
  // only pull recording info for the results that will display a clip.
  // whether it will show a clip is determined by there being a non-null
  // duration, and it must be one of the first 3 non-title matches, since
  // we only show up to 3 matches for a single search result.
  const resultsWithAtLeastOneVisibleClip = results.filter((result) => {
    const nonTitleMatches = filter(
      (m) => m.category !== "title",
      result.matches
    ).slice(0, MAX_VISIBLE_MATCHES);
    return some((m) => m.duration, nonTitleMatches);
  });

  return map("sessionId", resultsWithAtLeastOneVisibleClip);
};

const SearchResultList = ({ query, results }: SearchResultListProps) => {
  const { currentPage, goToNextPage: showNextPage } = usePagination(results);
  const numVisibleResults = (currentPage + 1) * NUM_RESULTS_PER_PAGE;
  const isSearching = useSearchMeetingsStore((state) => state.isSearching);

  const resultsToShow = results.slice(0, numVisibleResults);
  const recordingInfoQueries = useRecordingInfoQueries(
    getRecordingInfoSessionIds(resultsToShow)
  );

  return (
    <>
      <Stack
        sx={{
          opacity: isSearching ? 0.5 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        {resultsToShow.map((result) => (
          <SearchResult
            key={result.sessionId}
            query={query}
            recordingInfo={recordingInfoQueries.get(result.sessionId)}
            result={result}
          />
        ))}
      </Stack>
      {numVisibleResults < results.length && (
        <MoreResults onClick={showNextPage} />
      )}
    </>
  );
};

export default SearchResultList;
